/**
 * Self-update channel contract between the server's publishing endpoint
 * (packages/server/src/routes/app-update.ts, written by `deploy/publish-apk.sh`)
 * and the POS app's update checker (packages/pos/src/update).
 *
 * `AppUpdateManifest` is exactly the body `GET /api/v1/app/update` returns.
 * `url` is server-relative (e.g. `/app/download/pos-2.apk`) — the caller is
 * expected to resolve it against whatever base URL it already talks to the
 * API on, the same way every other endpoint in this app works.
 */
import { z } from 'zod';
import { isoDateTime } from './domain.js';
export const AppUpdateManifest = z.object({
    /** Android's monotonically increasing build number. The only field an
     * update decision is made on — never versionName, which is just a label. */
    versionCode: z.number().int().positive(),
    versionName: z.string().min(1),
    /** Lowercase hex sha256 of the exact APK bytes at `url`. Verified by the
     * client before the file is ever handed to the package installer. */
    sha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/, 'sha256 must be 64 lowercase hex characters'),
    /** Server-relative download path, e.g. "/app/download/pos-2.apk". */
    url: z.string().min(1),
    sizeBytes: z.number().int().nonnegative(),
    releasedAt: isoDateTime,
});
/**
 * Pure comparison, no I/O, no zod: exactly what a unit test wants to drive
 * directly. `current` is this install's own `versionCode` (from the native
 * `AppUpdater.getVersion()` call); `candidate` is a manifest's `versionCode`.
 */
export function compareVersions(current, candidate) {
    if (candidate > current)
        return 'newer';
    if (candidate === current)
        return 'same';
    return 'older';
}
/** Convenience wrapper over `compareVersions` for the common yes/no question. */
export function isNewerVersion(current, candidate) {
    return compareVersions(current, candidate) === 'newer';
}
//# sourceMappingURL=appUpdate.js.map